import { snakeize } from 'casing'
import {
  deleteSinglePrivateOffer,
  getPrivateOffersData,
  getSinglePrivateOffer,
  patchPrivateOffersData,
  sendPrivateOfferReminder,
} from '../../../api/marketplace'
import {
  getAzurePrivateOffersData,
  getAzureSingleContainerPrivateOffer,
  getAzureSinglePrivateOffer,
  getAzureSingleVmPrivateOffer,
  patchAzureContainerPrivateOffer,
  patchAzurePrivateOffersData,
  sendAzurePrivateOfferReminder,
} from '../../../api/azure'
import {
  getGCPPrivateOffersData,
  getGcpSinglePrivateOffer,
  patchGCPPrivateOffersData,
} from '../../../api/gcp'
import { isAzureContainerProduct } from '../../../components/MicrosoftFormComponent/ContainerPlan/containerPlanUtils'
import { isAzureVmProduct } from '../../../components/MicrosoftFormComponent/VmPlan/vmPlanUtils'
import type { CloudMarketplace, Product } from '../../productsListing/types'
import { getPrivateOffersSearchParams } from './privateOfferSearchParams'

type GetAzureSinglePrivateOffer = (
  partnerId: string,
  privateOfferId: string,
  crmOpportunityId?: string,
  productId?: string
) => Promise<{ data: Record<string, any> }>

export const getAzureSinglePrivateOfferApi = (
  product?: Product
): GetAzureSinglePrivateOffer => {
  if (isAzureVmProduct(product)) {
    return getAzureSingleVmPrivateOffer as GetAzureSinglePrivateOffer
  }

  if (isAzureContainerProduct(product)) {
    return getAzureSingleContainerPrivateOffer as GetAzureSinglePrivateOffer
  }

  return getAzureSinglePrivateOffer as GetAzureSinglePrivateOffer
}

export const getPrivateOfferAPIBasedOnCloud = async ({
  partnerId,
  cloud,
  privateOfferId,
  crmOpportunityId,
  productId,
  product,
  isContainerProduct,
}: {
  partnerId: string
  cloud: CloudMarketplace
  privateOfferId: string
  crmOpportunityId?: string
  productId?: string
  product?: Product
  isContainerProduct?: boolean
}) => {
  switch (cloud) {
    case 'AWS': {
      return getSinglePrivateOffer(partnerId, privateOfferId, crmOpportunityId)
    }
    case 'AZURE': {
      if (product) {
        const getPrivateOffer = getAzureSinglePrivateOfferApi(product)
        return getPrivateOffer(
          partnerId,
          privateOfferId,
          crmOpportunityId,
          productId
        )
      }

      return isContainerProduct
        ? getAzureSingleContainerPrivateOffer(
            partnerId,
            privateOfferId,
            crmOpportunityId,
            productId
          )
        : getAzureSinglePrivateOffer(
            partnerId,
            privateOfferId,
            crmOpportunityId,
            productId
          )
    }
    case 'GCP': {
      return getGcpSinglePrivateOffer(
        partnerId,
        privateOfferId,
        crmOpportunityId
      )
    }
  }
}

export const getPrivateOffersAPIBasedOnCloud = async (
  partnerId: string,
  cloud: CloudMarketplace,
  pageSize?: number,
  pageNumber?: number,
  productId?: string,
  crmOpportunityId?: string,
  expiringInDays?: string,
  offerStatus?: string[],
  input?: string,
  selectedInput?: string,
  _sectionName?: string
) => {
  switch (cloud) {
    case 'AWS': {
      // Private offers search now queries the marketplace service directly
      // (DB-backed) instead of going through the OpenSearch based search service.
      return getPrivateOffersData({
        partnerId,
        pageSize,
        pageNumber,
        productId,
        crmOpportunityId,
        expiringInDays,
        offerStatus,
        cloudType: 'AWS',
        ...getPrivateOffersSearchParams(input, selectedInput),
      })
    }
    case 'AZURE': {
      return getAzurePrivateOffersData({
        partnerId,
        pageSize,
        pageNumber,
        productId,
        crmOpportunityId,
        expiringInDays,
        offerStatus,
      })
    }
    case 'GCP': {
      return getGCPPrivateOffersData({
        partnerId,
        pageSize,
        pageNumber,
        productId,
        crmOpportunityId,
      })
    }
  }
}

export const patchOfferBasedOnCloud = async (
  cloud: CloudMarketplace,
  partnerId: string,
  privateOfferId: string,
  data: Record<string, unknown>,
  crmOpportunityId?: string,
  editOffer?: boolean,
  isContainerProduct = false
) => {
  switch (cloud) {
    case 'AWS': {
      return await patchPrivateOffersData(
        partnerId,
        privateOfferId,
        snakeize({ crmOpportunityId, ...data }),
        undefined,
        editOffer
      )
    }
    case 'AZURE': {
      const patchData = snakeize({ ...data })
      return isContainerProduct
        ? await patchAzureContainerPrivateOffer({
            partnerId,
            privateOfferId,
            data: patchData,
          })
        : await patchAzurePrivateOffersData(
            partnerId,
            privateOfferId,
            patchData
          )
    }
    case 'GCP': {
      return await patchGCPPrivateOffersData(
        partnerId,
        privateOfferId,
        snakeize({ crmOpportunityId, ...data })
      )
    }
  }
}

export const deleteOfferBasedOnCloud = async (
  cloud: CloudMarketplace,
  partnerId: string,
  privateOfferId: string
) => {
  switch (cloud) {
    case 'AWS': {
      return await deleteSinglePrivateOffer(partnerId, privateOfferId)
    }
    case 'AZURE': {
      return await patchAzurePrivateOffersData(
        partnerId,
        privateOfferId,
        snakeize({ state: 'DELETED' })
      )
    }
    case 'GCP': {
      // Yet to be implemented
      return
    }
  }
}

export const sendReminderBasedOnCloud = async (
  cloud: CloudMarketplace,
  partnerId: string,
  privateOfferId: string,
  data: Record<string, unknown>,
  resendOffer?: boolean
) => {
  switch (cloud) {
    case 'AWS': {
      return await sendPrivateOfferReminder(
        partnerId,
        privateOfferId,
        data,
        resendOffer
      )
    }
    case 'AZURE': {
      return await sendAzurePrivateOfferReminder(
        partnerId,
        privateOfferId,
        data,
        resendOffer
      )
    }
    case 'GCP': {
      return
    }
  }
}
